import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { GALLERY } from '../data/site';
import { TESTIDS } from '../constants/testIds';

const AUTOPLAY_MS = 5000;

export const Gallery = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = GALLERY.length;

  const go = useCallback((n) => setIndex((n + count) % count), [count]);
  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [next, paused]);

  return (
    <section
      id="gallery"
      data-testid={TESTIDS.gallerySection}
      className="py-20 sm:py-28 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
              / Recent work
            </span>
            <h2 className="font-display uppercase font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-4">
              Real jobs across <span className="text-[#E8B842]">Warwickshire</span>
            </h2>
          </div>
          <p className="text-[#A3A3A3] text-base sm:text-lg max-w-md">
            A selection of recent gutter cleans, fascia replacements and roof moss removals
            in and around Leamington Spa.
          </p>
        </div>

        <div
          className="relative bg-[#0E0E0E] border border-[#E8B842]/15 overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="gallery-slideshow"
        >
          {/* Slides */}
          <div className="relative aspect-[16/10] sm:aspect-[16/9] bg-black">
            {GALLERY.map((img, i) => (
              <img
                key={img.url}
                src={img.url}
                alt={img.alt}
                loading={i === 0 ? 'eager' : 'lazy'}
                data-testid={`${TESTIDS.galleryImage}-${i}`}
                className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${
                  i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              />
            ))}

            {/* Counter */}
            <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm text-white font-display uppercase tracking-widest text-xs px-3 py-1.5 border border-[#E8B842]/30">
              <span className="text-[#E8B842]">{String(index + 1).padStart(2, '0')}</span>
              <span className="text-[#A3A3A3]"> / {String(count).padStart(2, '0')}</span>
            </div>

            {/* Prev / Next */}
            <button
              type="button"
              onClick={prev}
              data-testid="gallery-prev"
              aria-label="Previous photo"
              className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 bg-black/60 hover:bg-[#E8B842] hover:text-black text-white backdrop-blur-sm border border-[#E8B842]/40 flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={next}
              data-testid="gallery-next"
              aria-label="Next photo"
              className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 bg-black/60 hover:bg-[#E8B842] hover:text-black text-white backdrop-blur-sm border border-[#E8B842]/40 flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 py-5 bg-[#0A0A0A] border-t border-[#E8B842]/10">
            {GALLERY.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => go(i)}
                aria-label={`Go to photo ${i + 1}`}
                data-testid={`gallery-dot-${i}`}
                className={`h-1.5 transition-all ${
                  i === index
                    ? 'w-10 bg-[#E8B842]'
                    : 'w-5 bg-[#2a2a2a] hover:bg-[#4a4a4a]'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
